// Nachfrage zu Vorgangs-Ordnern, die der Automatismus nicht deuten konnte
// (Plan 01.10.2026, C2): je Ordner Betreff, Jahr, Offen | Erledigt und ein
// Haken „anlegen". Erscheint im Zuweisen-Popup direkt nach der Zuweisung und
// in der Akte, solange etwas offen ist. Angelegt wird vom Scheduler, sobald
// die Dokumente des Ordners gebucht sind.

import * as React from "react";
import { useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Z_LAYERS } from "../windowManager/zLayers";
import { FolderIcon } from "../documents/explorer/fileTypeIcons";
import {
  kundenaktenVorgangService,
  type KundenaktenVorgangEintrag,
  type VorgangEntscheidung,
} from "../../services/kundenaktenVorgangService";

type Zeile = { relPfad: string; name: string; grund: string | null; anlegen: boolean; titel: string; jahr: string; status: "Offen" | "Erledigt" };

const zeilenAus = (eintraege: KundenaktenVorgangEintrag[]): Zeile[] =>
  eintraege.map((e) => ({
    relPfad: e.relPfad,
    name: e.name,
    grund: e.grund,
    anlegen: e.status !== "verworfen",
    titel: e.titel ?? e.name,
    jahr: e.jahr ? String(e.jahr) : "",
    status: e.vorgangStatus === "Erledigt" ? "Erledigt" : "Offen",
  }));

/** Die Entscheidungen aus den Zeilen — nur was anlegen soll, braucht einen Betreff. */
export const entscheidungenAus = (zeilen: Zeile[]): VorgangEntscheidung[] =>
  zeilen.map((z) => ({
    relPfad: z.relPfad,
    anlegen: z.anlegen,
    titel: z.titel.trim(),
    status: z.status,
    jahr: /^\d{4}$/.test(z.jahr.trim()) ? Number(z.jahr.trim()) : null,
  }));

export const VorgaengeEntscheiden: React.FC<{
  folderId: number;
  eintraege: KundenaktenVorgangEintrag[];
  onFertig: () => void;
  /** „Später": schließt ohne zu speichern — die Nachfrage bleibt in der Akte. */
  onSpaeter: () => void;
}> = ({ folderId, eintraege, onFertig, onSpaeter }) => {
  const [zeilen, setZeilen] = useState<Zeile[]>(() => zeilenAus(eintraege));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const setZeile = (i: number, teil: Partial<Zeile>) => setZeilen((alt) => alt.map((z, k) => (k === i ? { ...z, ...teil } : z)));
  const unvollstaendig = zeilen.some((z) => z.anlegen && !z.titel.trim());

  const speichern = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await kundenaktenVorgangService.entscheiden(folderId, entscheidungenAus(zeilen));
      onFertig();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Entscheidung konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="ak-pop-hinweis">
        Diese Ordner sehen nach Vorgängen aus, ließen sich aber nicht eindeutig deuten. Bitte Betreff, Jahr und Stand
        prüfen; was angehakt ist, wird als Vorgang angelegt, sobald die Dokumente gebucht sind.
      </div>
      <div className="ak-vo-liste">
        {zeilen.map((z, i) => (
          <div key={z.relPfad} className={"ak-vo-zeile" + (z.anlegen ? "" : " aus")}>
            <label className="ak-vo-kopf">
              <input type="checkbox" checked={z.anlegen} onChange={(e) => setZeile(i, { anlegen: e.target.checked })} />
              <FolderIcon size={18} />
              <span className="ak-vo-name" title={z.relPfad}>{z.relPfad}</span>
              {z.grund && <span className="ak-muted">{z.grund}</span>}
            </label>
            <div className="ak-vo-felder">
              <label className="ak-vo-feld" style={{ flex: "1 1 260px" }}>
                <span>Betreff</span>
                <input type="text" value={z.titel} disabled={!z.anlegen} onChange={(e) => setZeile(i, { titel: e.target.value })} />
              </label>
              <label className="ak-vo-feld" style={{ flex: "0 0 96px" }}>
                <span>Jahr</span>
                <input type="text" inputMode="numeric" placeholder="JJJJ" value={z.jahr} disabled={!z.anlegen} onChange={(e) => setZeile(i, { jahr: e.target.value })} />
              </label>
              <div className="docx-seg" role="group" aria-label="Stand">
                {(["Offen", "Erledigt"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={"docx-seg-btn" + (z.status === s ? " on" : "")}
                    aria-pressed={z.status === s}
                    disabled={!z.anlegen}
                    onClick={() => setZeile(i, { status: s })}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {fehler && <div className="ak-fehler" role="alert">{fehler}</div>}
      <div className="ak-vo-foot">
        <button type="button" className="tbtn" onClick={onSpaeter} disabled={busy}>
          Später
        </button>
        <button type="button" className="tbtn primary" onClick={() => void speichern()} disabled={busy || unvollstaendig}>
          {busy ? "Bitte warten …" : "Übernehmen"}
        </button>
      </div>
    </>
  );
};

/** Dieselbe Nachfrage als eigenes Popup (aus der Akte). */
export const VorgaengeEntscheidenPopup: React.FC<{
  folderId: number;
  folderName: string;
  eintraege: KundenaktenVorgangEintrag[];
  onClose: () => void;
  onEntschieden: () => void;
}> = ({ folderId, folderName, eintraege, onClose, onEntschieden }) =>
  createPortal(
    <div className="kvp-rd kvp-rd-scrim" style={{ zIndex: Z_LAYERS.windowConfirm }} data-testid="vorgaenge-scrim">
      <div className="dm" role="dialog" aria-modal="true" aria-labelledby="vorgaenge-titel" style={{ width: "min(760px, 100%)" }}>
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Alte Kundenakte · {folderName}</div>
              <h2 id="vorgaenge-titel">Vorgänge aus dieser Akte</h2>
            </div>
            <button type="button" className="dm-x" onClick={onClose} aria-label="Schließen">
              ×
            </button>
          </div>
        </div>
        <div className="dm-body" style={{ display: "grid", gap: 14 }}>
          <VorgaengeEntscheiden folderId={folderId} eintraege={eintraege} onFertig={onEntschieden} onSpaeter={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  );
